const LOGO =
  'https://storage.readdy-site.link/project_files/ef2cd86a-0c8c-43d9-a80f-76980d5cdfeb/efd1c1b3-eb7c-4977-9e9d-97ff5b41d004_OnPoint-Logo-Red-White-and-Blue.png?v=a3ea8d6e61ccf94fd7ba238e66b5c0b3';

const stats = [
  { value: '$24B+', label: 'Global market size by 2030', icon: 'ri-line-chart-fill' },
  { value: '65%+', label: 'Reduction in chronic inflammation reported', icon: 'ri-heart-pulse-fill' },
  { value: '10x', label: 'Faster tissue repair vs. conventional methods', icon: 'ri-timer-flash-fill' },
  { value: '200M+', label: 'Patients treated with biologics globally', icon: 'ri-group-fill' },
];

const pillars = [
  {
    icon: 'ri-dna-fill',
    title: 'DNA-Based Biologics',
    color: '#dc2626',
    desc: 'DNA-based biologics harness the body\'s own genetic blueprint to trigger targeted healing responses. Unlike pharmaceuticals that mask symptoms, these therapies instruct cells at the DNA level to regenerate, repair, and rebuild damaged tissue — working with biology, not against it.',
  },
  {
    icon: 'ri-microscope-fill',
    title: 'Cellular Signaling Science',
    color: '#002868',
    desc: 'Exosomes and growth factors carry molecular instructions — essentially biological messages — that tell surrounding cells how to respond to injury or disease. This precision cellular communication is the cornerstone of next-generation regenerative therapy.',
  },
  {
    icon: 'ri-leaf-fill',
    title: 'Natural & Minimally Invasive',
    color: '#dc2626',
    desc: 'Regenerative therapies like amniotic allografts, Wharton\'s Jelly MSCs, and PRP are derived from natural biological sources. They integrate seamlessly with the body\'s own systems, dramatically reducing rejection risk and side effects compared to synthetic drugs or invasive surgery.',
  },
  {
    icon: 'ri-flask-2-fill',
    title: 'Stem Cell Breakthroughs',
    color: '#002868',
    desc: 'Mesenchymal stem cells (MSCs) from umbilical cord tissue can differentiate into bone, cartilage, and muscle cells — making them a revolutionary tool for orthopedic, neurological, and systemic conditions previously considered untreatable or managed only with lifelong medication.',
  },
  {
    icon: 'ri-shield-cross-fill',
    title: 'Immune System Modulation',
    color: '#dc2626',
    desc: 'Many regenerative biologics have proven immunomodulatory properties — they help regulate overactive immune responses responsible for autoimmune conditions, chronic inflammation, and post-surgical complications, offering relief where traditional medicine falls short.',
  },
  {
    icon: 'ri-user-heart-fill',
    title: 'Patient-Centered Outcomes',
    color: '#002868',
    desc: 'Studies consistently show patients undergoing regenerative therapy report higher quality of life, faster recovery, reduced dependency on opioids and NSAIDs, and longer-lasting relief — shifting the entire paradigm from disease management to genuine healing.',
  },
];

const conditions = [
  'Osteoarthritis & Joint Degeneration',
  'Chronic Tendon & Ligament Injuries',
  'Spinal Disc Degeneration',
  'Non-Healing Wounds & Ulcers',
  'Autoimmune Inflammation',
  'Post-Surgical Recovery',
  'Neuropathic Pain',
  'Aesthetic & Anti-Aging Applications',
];

export default function WhyRegenMed() {
  return (
    <section className="relative overflow-hidden" style={{ background: '#001a4d' }}>

      {/* DNA watermark behind everything */}
      <img
        src={LOGO}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute"
        style={{
          right: '-120px',
          top: '50%',
          transform: 'translateY(-50%) rotate(15deg)',
          width: '700px',
          opacity: 0.06,
          filter: 'saturate(4) brightness(1.4)',
          mixBlendMode: 'screen',
          zIndex: 0,
        }}
      />
      <img
        src={LOGO}
        alt=""
        aria-hidden="true"
        className="pointer-events-none select-none absolute"
        style={{
          left: '-100px',
          bottom: '-80px',
          width: '420px',
          opacity: 0.04,
          filter: 'saturate(4) brightness(1.4)',
          mixBlendMode: 'screen',
          transform: 'rotate(-10deg)',
          zIndex: 0,
        }}
      />

      {/* ── Section 1: Intro ── */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 pt-24 pb-16 text-center">
        <span className="inline-flex items-center gap-2 bg-red-600/20 border border-red-500/30 text-red-400 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">
          <i className="ri-dna-fill" /> The Science Behind the Future
        </span>
        <h2
          className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          Why Regenerative Medicine<br />
          <span style={{ color: '#f87171' }}>Is Changing Everything</span>
        </h2>
        <p className="text-blue-200 text-sm leading-relaxed max-w-3xl mx-auto">
          For decades, medicine focused on managing disease — suppressing symptoms with drugs, replacing joints with hardware, or cutting out the problem. Regenerative Medicine takes a radically different approach: it gives the body the biological tools it needs to <strong className="text-white">heal itself at the cellular and DNA level.</strong> This is not the future. It&apos;s happening now — and OnPoint USA is at the forefront.
        </p>
      </div>

      {/* ── Stats Bar ── */}
      <div className="relative z-10 border-y border-blue-800/60" style={{ background: 'rgba(0,40,104,0.5)' }}>
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div key={stat.value} className="text-center">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg mx-auto mb-2" style={{ background: '#dc2626' }}>
                <i className={`${stat.icon} text-white text-base`} />
              </div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {stat.value}
              </div>
              <div className="text-blue-300 text-xs leading-snug">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── 6 Science Pillars ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            The Six Pillars of Regenerative Science
          </h3>
          <div className="w-12 h-0.5 bg-red-500 mx-auto mt-3" />
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {pillars.map((p) => (
            <div
              key={p.title}
              className="rounded-xl p-6 border transition-all duration-300 hover:border-red-500/40"
              style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}
            >
              <div
                className="w-11 h-11 flex items-center justify-center rounded-lg mb-4"
                style={{ background: p.color }}
              >
                <i className={`${p.icon} text-white text-lg`} />
              </div>
              <h4 className="text-white font-bold text-sm mb-2">{p.title}</h4>
              <p className="text-blue-200 text-xs leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Conditions Section ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16">
        <div
          className="rounded-2xl p-10 grid md:grid-cols-2 gap-10 items-center"
          style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.2)' }}
        >
          {/* Left: image */}
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=macro%20close%20up%20of%20DNA%20double%20helix%20strand%20glowing%20in%20deep%20blue%20and%20red%20light%2C%20molecular%20biology%20genetic%20science%20visualization%2C%20sharp%20detailed%20abstract%20biological%20structure%2C%20dark%20background%20with%20vibrant%20glowing%20colors%2C%20photorealistic%20scientific%20imagery&width=640&height=420&seq=regen-dna-01&orientation=landscape"
              alt="DNA double helix regenerative medicine"
              className="rounded-xl w-full h-64 object-cover object-top"
            />
            <div
              className="absolute inset-0 rounded-xl"
              style={{ background: 'linear-gradient(120deg, rgba(0,26,77,0.3) 0%, transparent 60%)' }}
            />
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 rounded-full px-3 py-1.5">
              <i className="ri-dna-fill text-white text-xs" />
              <span className="text-white text-xs font-bold">DNA-Level Healing</span>
            </div>
          </div>

          {/* Right: conditions list */}
          <div>
            <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Conditions Addressed by Regenerative Medicine
            </h3>
            <p className="text-blue-200 text-xs leading-relaxed mb-6">
              The scope of regenerative therapy is expanding rapidly. Today, clinicians are applying biologics across a broad spectrum of conditions with remarkable results:
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {conditions.map((c) => (
                <li key={c} className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-dna-fill text-red-400 text-xs" />
                  </div>
                  <span className="text-blue-100 text-xs">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ── Bottom CTA strip ── */}
      <div
        className="relative z-10 py-10 px-6 text-center border-t"
        style={{ borderColor: 'rgba(255,255,255,0.08)' }}
      >
        <p className="text-blue-200 text-sm mb-1">
          Ready to bring the power of Regenerative Medicine to your practice?
        </p>
        <p className="text-white font-bold text-base mb-6">
          OnPoint USA has the products, the expertise, and the connections to get you there.
        </p>
        <a
          href="/products-and-services"
          className="inline-block text-white font-bold text-sm px-8 py-3 rounded-md hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          style={{ background: '#dc2626' }}
        >
          Explore Our Products &amp; Services
        </a>
      </div>

    </section>
  );
}
